import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { esCount } from '@/lib/es-count';
import { notify } from '@/lib/notify';
import { apiClient } from '@/services/api-client';
import type { CatalogSupplierRow } from '../catalog/types';
import { TEMPLATE_HELP_PATH } from './templateManual';
import { unzipPhotos } from './unzipPhotos';

interface FeedPreview {
    products: number;
    rejected: number;
    created: number;
    priceChanges: number;
    stockChanges: number;
    availabilityChanges: number;
    unchanged: number;
    absent: number;
    samples: {
        rejected: { line: number; externalId: string; reason: string }[];
        absent: string[];
    };
}

interface CsvResponse {
    ok: boolean;
    datedPath?: string;
    preview?: FeedPreview;
}

interface PhotoResponse {
    stored: number;
    matched: number;
    csvUpdated: boolean;
    skipped: { name: string; reason: string }[];
}

interface PhotoInventory {
    stored: number;
    cited: number;
    missing: string[];
}

const PHOTO_BATCH = 12;

function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    const size = 0x2000;
    for (let i = 0; i < bytes.length; i += size) {
        binary += String.fromCharCode(...bytes.subarray(i, i + size));
    }
    return btoa(binary);
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
    // slice() copia a un ArrayBuffer propio: Uint8Array no entra directo en Blob (TS 5.9).
    const stream = new Blob([data.slice()]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readPhotoFiles(files: File[]): Promise<{ name: string; bytes: Uint8Array }[]> {
    const photos: { name: string; bytes: Uint8Array }[] = [];
    for (const file of files) {
        const bytes = new Uint8Array(await file.arrayBuffer());
        if (file.name.toLowerCase().endsWith('.zip')) {
            photos.push(...await unzipPhotos(bytes, inflateRaw));
        } else {
            photos.push({ name: file.name, bytes });
        }
    }
    return photos;
}

export function SupplierTemplateUpload({
    supplier,
    onRecord,
}: {
    supplier: CatalogSupplierRow;
    onRecord?: (notice: { type: 'ok' | 'err'; text: string }) => void;
}) {
    const csvInput = useRef<HTMLInputElement>(null);
    const photoInput = useRef<HTMLInputElement>(null);
    const [busy, setBusy] = useState<'csv' | 'photos' | null>(null);
    const [progress, setProgress] = useState('');
    const [preview, setPreview] = useState<FeedPreview | null>(null);
    const [inventory, setInventory] = useState<PhotoInventory | null>(null);

    const loadInventory = async () => {
        const res = await apiClient.post<PhotoInventory & { ok?: boolean }>('store/admin/catalog-feed', {
            supplier: supplier.slug,
            inventory: true,
        });
        if (res.error || !res.data) return;
        setInventory({
            stored: res.data.stored ?? 0,
            cited: res.data.cited ?? 0,
            missing: res.data.missing ?? [],
        });
    };

    useEffect(() => {
        void loadInventory();
    }, [supplier.slug]);

    const uploadCsv = async (file: File) => {
        setBusy('csv');
        setProgress('');
        try {
            const csv = await file.text();
            const res = await apiClient.post<CsvResponse>('store/admin/catalog-feed', {
                supplier: supplier.slug,
                csv,
            });
            if (res.error) throw new Error(res.error.message);
            setPreview(res.data?.preview ?? null);
            const notice = {
                type: 'ok' as const,
                text: 'Plantilla guardada. Revisa la previsualización y pulsa Sincronizar ahora para aplicarla.',
            };
            onRecord?.(notice);
            notify(notice);
        } catch (error: unknown) {
            const notice = { type: 'err' as const, text: error instanceof Error ? error.message : String(error) };
            onRecord?.(notice);
            notify(notice);
        } finally {
            setBusy(null);
            void loadInventory();
        }
    };

    const uploadPhotos = async (files: File[]) => {
        setBusy('photos');
        try {
            const photos = await readPhotoFiles(files);
            if (!photos.length) throw new Error('El archivo no contiene fotos jpg, png o webp');
            let stored = 0;
            let matched = 0;
            let skipped = 0;
            for (let i = 0; i < photos.length; i += PHOTO_BATCH) {
                const batch = photos.slice(i, i + PHOTO_BATCH);
                setProgress(`${Math.min(i + batch.length, photos.length)} de ${photos.length}`);
                const res = await apiClient.post<PhotoResponse>('store/admin/catalog-feed', {
                    supplier: supplier.slug,
                    photos: batch.map((photo) => ({
                        name: photo.name,
                        base64: bytesToBase64(photo.bytes),
                    })),
                });
                if (res.error) throw new Error(res.error.message);
                stored += res.data?.stored ?? 0;
                matched += res.data?.matched ?? 0;
                skipped += res.data?.skipped?.length ?? 0;
            }
            const pending = matched === 0 ? ' Aún no hay un CSV que cite estos nombres.' : '';
            const notice = {
                type: 'ok' as const,
                text: `${esCount(stored, 'foto guardada', 'fotos guardadas')} · ${matched} con el mismo nombre en la plantilla${skipped ? ` · ${skipped} omitidas` : ''}.${pending}`,
            };
            onRecord?.(notice);
            notify(notice);
        } catch (error: unknown) {
            const notice = { type: 'err' as const, text: error instanceof Error ? error.message : String(error) };
            onRecord?.(notice);
            notify(notice);
        } finally {
            setBusy(null);
            setProgress('');
            void loadInventory();
        }
    };

    return (
        <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
                Sube el ZIP de fotos y después la plantilla CSV. La previsualización no aplica el catálogo.
                {' '}
                <Link className="text-primary underline" to={TEMPLATE_HELP_PATH}>Instrucciones y plantilla</Link>
            </p>
            <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" disabled={busy != null} onClick={() => photoInput.current?.click()}>
                    {busy === 'photos' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Subir fotos
                </Button>
                <Button type="button" size="sm" disabled={busy != null} onClick={() => csvInput.current?.click()}>
                    {busy === 'csv' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Subir plantilla CSV
                </Button>
                {progress ? <span className="self-center text-xs text-muted-foreground">Fotos {progress}</span> : null}
            </div>
            <input
                ref={photoInput}
                className="sr-only"
                type="file"
                accept=".zip,image/jpeg,image/png,image/webp"
                multiple
                onChange={(event) => {
                    const files = [...(event.target.files ?? [])];
                    event.target.value = '';
                    if (files.length) void uploadPhotos(files);
                }}
            />
            <input
                ref={csvInput}
                className="sr-only"
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void uploadCsv(file);
                }}
            />
            {preview ? <TemplatePreview preview={preview} /> : null}
            {inventory ? (
                <section className="space-y-2 rounded-xl border border-border px-3 py-2">
                    <p className="text-sm">
                        {esCount(inventory.stored, 'foto en Blob', 'fotos en Blob')}
                        {' · '}
                        {esCount(inventory.cited, 'citada por la plantilla', 'citadas por la plantilla')}
                        {' · '}
                        {esCount(inventory.missing.length, 'nombre sin foto', 'nombres sin foto')}
                    </p>
                    {inventory.missing.length > 0 ? (
                        <ul className="max-h-40 overflow-auto font-mono text-xs text-muted-foreground">
                            {inventory.missing.map((name) => (
                                <li key={name}>{name}</li>
                            ))}
                        </ul>
                    ) : null}
                </section>
            ) : null}
        </div>
    );
}

function TemplatePreview({ preview }: { preview: FeedPreview }) {
    const rows = [
        ['Productos en el archivo', preview.products],
        ['Altas', preview.created],
        ['Cambios de precio', preview.priceChanges],
        ['Cambios de stock', preview.stockChanges],
        ['Cambios de disponibilidad', preview.availabilityChanges],
        ['Sin cambios', preview.unchanged],
        ['Filas rechazadas', preview.rejected],
        ['Fuera del archivo', preview.absent],
    ] as const;
    return (
        <div className="space-y-2 rounded-lg border border-border p-3">
            <h2 className="text-sm font-semibold">Previsualización</h2>
            <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                {rows.map(([label, value]) => (
                    <div key={label}>
                        <dt className="text-xs text-muted-foreground">{label}</dt>
                        <dd className="font-semibold tabular-nums">{value}</dd>
                    </div>
                ))}
            </dl>
            {preview.samples.rejected.length > 0 ? (
                <ul className="space-y-1 text-sm text-muted-foreground">
                    {preview.samples.rejected.map((row) => (
                        <li key={`${row.line}-${row.reason}`}>
                            Fila {row.line}{row.externalId ? ` · ${row.externalId}` : ''} · {row.reason}
                        </li>
                    ))}
                </ul>
            ) : null}
            {preview.absent > 0 ? (
                <p className="text-sm text-muted-foreground">
                    Fuera del archivo: {preview.samples.absent.join(', ')}
                    {preview.absent > preview.samples.absent.length ? '…' : ''}.
                    Si son muchas, la sincronización no las da de baja sola.
                </p>
            ) : null}
        </div>
    );
}
